// --- Imports ---
import { Box, Stack, Typography, useTheme, useMediaQuery } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLanguageStore } from "../../../../Store/store";
import HeroContent from "./HeroContent";
import HeroPicture from "./HeroPicture";
import { motion } from "framer-motion";

// --- Hero Component ---
export default function Hero() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme?.breakpoints?.down("lg"));
  const { language } = useLanguageStore();

  return (
    <Stack
      id="home-section"
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        // backgroundColor: "primary.dark",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* --- Dynamic Aurora Background --- */}
      <Box
        component={motion.div}
        animate={{
          backgroundPosition: [
            "0% 0%",
            "100% 100%",
            "0% 100%",
            "100% 0%",
            "0% 0%",
          ],
        }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
        sx={{
          position: "absolute",
          inset: "-50%",
          background: `
            radial-gradient(circle at 50% 50%, ${theme?.palette?.primary?.dark}40 0%, transparent 40%),
            radial-gradient(circle at 80% 20%, ${theme?.palette?.secondary?.dark}40 0%, transparent 30%),
            radial-gradient(circle at 20% 80%, ${theme?.palette?.primary?.main}30 0%, transparent 40%)
          `,
          backgroundSize: "200% 200%",
          filter: "blur(60px)",
          opacity: 0.7,
          zIndex: 0,
        }}
      />

      {/* --- Perspective Grid --- */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(to right, ${theme?.palette?.divider}10 1px, transparent 1px),
            linear-gradient(to bottom, ${theme?.palette?.divider}10 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
          transform:
            "perspective(1000px) rotateX(75deg) scale(2.5) translateY(-200px)",
          transformOrigin: "top center",
          opacity: 0.4,
          zIndex: 0,
        }}
      />

      {/* --- Main Content --- */}
      <Stack
        direction={isMobile ? "column" : "row"}
        alignItems="center"
        justifyContent="space-between"
        sx={{
          width: "100%",
          maxWidth: "1400px",
          padding: { xs: theme?.spacing(4), lg: theme?.spacing(8) },
          gap: { xs: 8, lg: 6 },
          zIndex: 10,
        }}
      >
        <HeroContent />
        <HeroPicture />
      </Stack>

      {/* --- High-Tech Scroll Indicator --- */}
      <Box
        component={motion.div}
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        sx={{
          position: "absolute",
          bottom: "40px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          zIndex: 10,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            letterSpacing: 6,
            color: theme?.palette?.primary?.main,
            textShadow: `0 0 10px ${theme?.palette?.primary?.main}80`,
            fontWeight: 800,
          }}
        >
          SCROLL
        </Typography>
        <Box
          sx={{
            width: "2px",
            height: "60px",
            background: `linear-gradient(to bottom, ${theme?.palette?.primary?.main}, transparent)`,
            position: "relative",
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: "-1px",
              width: "4px",
              height: "20px",
              background: "#fff",
              boxShadow: `0 0 15px 3px ${theme?.palette?.primary?.main}`,
              animation: "laserDrop 2s ease-out infinite",
            },
            "@keyframes laserDrop": {
              "0%": { transform: "translateY(-20px)", opacity: 1 },
              "100%": { transform: "translateY(80px)", opacity: 0 },
            },
          }}
        />
      </Box>
    </Stack>
  );
}
